import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Trash2, Star, StarOff, Search } from "lucide-react";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import ImageUploader, { resolveAssetUrl } from "@/components/ImageUploader";

const EMPTY = {
  title_en: "",
  title_ar: "",
  method: "v60",
  difficulty: "easy",
  image_url: "",
  ingredients_en: [],
  ingredients_ar: [],
  steps_en: [],
  steps_ar: [],
  params: {},
  is_community: true,
};

export default function AdminRecipesTab() {
  const { isRTL } = useI18n();
  const qc = useQueryClient();
  const { data: recipes = [], isLoading } = useQuery({
    queryKey: ["admin", "recipes"],
    queryFn: endpoints.adminListAllRecipes,
  });
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | curated | community
  const [editing, setEditing] = useState(null);

  const refetch = () => {
    qc.invalidateQueries({ queryKey: ["admin", "recipes"] });
    qc.invalidateQueries({ queryKey: ["recipes"] });
  };

  const filtered = useMemo(() => {
    let arr = recipes;
    if (filter === "curated") arr = arr.filter((r) => !r.is_community);
    if (filter === "community") arr = arr.filter((r) => r.is_community);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      arr = arr.filter(
        (r) =>
          (r.title_en || "").toLowerCase().includes(needle) ||
          (r.title_ar || "").toLowerCase().includes(needle) ||
          (r.user_name || "").toLowerCase().includes(needle)
      );
    }
    return arr;
  }, [recipes, q, filter]);

  const onDelete = async (id) => {
    if (!window.confirm(isRTL ? "حذف هذه الوصفة؟" : "Delete this recipe?")) return;
    try {
      await endpoints.adminDeleteRecipe(id);
      toast.success(isRTL ? "تم الحذف" : "Deleted");
      refetch();
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  const onToggleCurated = async (r) => {
    try {
      await endpoints.adminSetRecipeCurated(r.id, r.is_community); // promote to curated if community
      toast.success(isRTL ? "تم التحديث" : "Updated");
      refetch();
    } catch {
      toast.error(isRTL ? "تعذّر التحديث" : "Update failed");
    }
  };

  const onSave = async (form) => {
    try {
      const payload = {
        ...form,
        ingredients_en: Array.isArray(form.ingredients_en)
          ? form.ingredients_en
          : (form.ingredients_en || "").split("\n").filter(Boolean),
        ingredients_ar: Array.isArray(form.ingredients_ar)
          ? form.ingredients_ar
          : (form.ingredients_ar || "").split("\n").filter(Boolean),
        steps_en: Array.isArray(form.steps_en)
          ? form.steps_en
          : (form.steps_en || "").split("\n").filter(Boolean),
        steps_ar: Array.isArray(form.steps_ar)
          ? form.steps_ar
          : (form.steps_ar || "").split("\n").filter(Boolean),
        params: typeof form.params === "string" ? JSON.parse(form.params || "{}") : form.params,
      };
      await endpoints.updateRecipe(editing.id, payload);
      toast.success(isRTL ? "تم الحفظ" : "Saved");
      refetch();
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الحفظ" : "Save failed"));
    }
  };

  if (isLoading) return <div className="text-[#5C544F]">…</div>;

  return (
    <div data-testid="admin-recipes-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-2xl font-semibold text-[#2A2421]">
          {isRTL ? "الوصفات" : "Recipes"} · {recipes.length}
        </h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute top-2.5 start-2 w-4 h-4 text-[#5C544F]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={isRTL ? "بحث…" : "Search…"}
              className="ps-8 w-48"
              data-testid="recipes-search"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-md border px-2 py-1.5 text-sm"
            data-testid="recipes-filter"
          >
            <option value="all">{isRTL ? "الكل" : "All"}</option>
            <option value="curated">{isRTL ? "المختارة" : "Curated"}</option>
            <option value="community">{isRTL ? "المجتمع" : "Community"}</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="recipes-table">
          <thead>
            <tr className="border-b border-[#2A2421]/10 text-[10px] uppercase tracking-widest text-[#5C544F]">
              <th className="text-start py-2">{isRTL ? "العنوان" : "Title"}</th>
              <th className="text-start py-2">{isRTL ? "الطريقة" : "Method"}</th>
              <th className="text-start py-2">{isRTL ? "المستخدم" : "User"}</th>
              <th className="text-start py-2">{isRTL ? "نوع" : "Type"}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-[#2A2421]/5 hover:bg-[#F9F6F0]/50" data-testid={`recipe-row-${r.id}`}>
                <td className="py-2">
                  <div className="flex items-center gap-2">
                    {r.image_url ? (
                      <img src={resolveAssetUrl(r.image_url)} alt="" className="w-10 h-10 rounded object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded bg-[#F9F6F0]" />
                    )}
                    <div>
                      <div className="font-medium text-[#2A2421]">{isRTL ? r.title_ar : r.title_en}</div>
                      <div className="text-[10px] text-[#5C544F]">{r.difficulty}</div>
                    </div>
                  </div>
                </td>
                <td className="py-2">{r.method}</td>
                <td className="py-2">{r.user_name || "—"}</td>
                <td className="py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.is_community ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                    {r.is_community ? (isRTL ? "مجتمع" : "Community") : (isRTL ? "مختارة" : "Curated")}
                  </span>
                </td>
                <td className="py-2 text-end">
                  <Button size="sm" variant="ghost" onClick={() => onToggleCurated(r)} title={r.is_community ? "Promote" : "Demote"} data-testid={`recipe-curated-${r.id}`}>
                    {r.is_community ? <Star className="w-4 h-4" /> : <StarOff className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`recipe-edit-${r.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDelete(r.id)} data-testid={`recipe-delete-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="py-8 text-center text-[#5C544F]">
                  {isRTL ? "لا توجد وصفات" : "No recipes"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <RecipeDialog
          key={editing.id}
          editing={editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
          isRTL={isRTL}
        />
      )}
    </div>
  );
}

function RecipeDialog({ editing, onClose, onSave, isRTL }) {
  const [form, setForm] = useState({
    ...EMPTY,
    ...editing,
    ingredients_en: (editing.ingredients_en || []).join("\n"),
    ingredients_ar: (editing.ingredients_ar || []).join("\n"),
    steps_en: (editing.steps_en || []).join("\n"),
    steps_ar: (editing.steps_ar || []).join("\n"),
    params: JSON.stringify(editing.params || {}, null, 2),
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="recipe-dialog">
        <DialogHeader>
          <DialogTitle>{isRTL ? "تعديل الوصفة" : "Edit recipe"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={isRTL ? "العنوان (عربي)" : "Title (AR)"} value={form.title_ar} onChange={set("title_ar")} testid="recipe-title-ar" />
            <Field label={isRTL ? "العنوان (إنجليزي)" : "Title (EN)"} value={form.title_en} onChange={set("title_en")} testid="recipe-title-en" />
            <Field label={isRTL ? "الطريقة" : "Method"} value={form.method} onChange={set("method")} testid="recipe-method" />
            <Field label={isRTL ? "الصعوبة" : "Difficulty"} value={form.difficulty} onChange={set("difficulty")} testid="recipe-difficulty" />
            <div>
              <label className="text-xs uppercase tracking-widest text-[#5C544F]">{isRTL ? "نوع" : "Type"}</label>
              <select
                value={form.is_community ? "1" : "0"}
                onChange={(e) => setForm((f) => ({ ...f, is_community: e.target.value === "1" }))}
                className="w-full rounded-md border px-2 py-2 text-sm"
                data-testid="recipe-type"
              >
                <option value="0">{isRTL ? "مختارة" : "Curated"}</option>
                <option value="1">{isRTL ? "مجتمع" : "Community"}</option>
              </select>
            </div>
          </div>
          <TextField label={isRTL ? "المكوّنات (عربي - سطر لكل واحد)" : "Ingredients AR (one per line)"} value={form.ingredients_ar} onChange={set("ingredients_ar")} testid="recipe-ing-ar" />
          <TextField label={isRTL ? "المكوّنات (إنجليزي)" : "Ingredients EN"} value={form.ingredients_en} onChange={set("ingredients_en")} testid="recipe-ing-en" />
          <TextField label={isRTL ? "الخطوات (عربي)" : "Steps AR"} value={form.steps_ar} onChange={set("steps_ar")} testid="recipe-steps-ar" />
          <TextField label={isRTL ? "الخطوات (إنجليزي)" : "Steps EN"} value={form.steps_en} onChange={set("steps_en")} testid="recipe-steps-en" />
          <TextField label={isRTL ? "البارامترات (JSON)" : "Params (JSON)"} value={form.params} onChange={set("params")} testid="recipe-params" mono />
          <ImageUploader
            label={isRTL ? "صورة الوصفة" : "Recipe image"}
            value={form.image_url}
            onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
            testid="recipe-image-uploader"
            aspect="video"
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="recipe-cancel-btn">{isRTL ? "إلغاء" : "Cancel"}</Button>
            <Button onClick={() => onSave(form)} className="bg-[#2A2421] text-[#F9F6F0]" data-testid="recipe-save-btn">
              {isRTL ? "حفظ" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#5C544F]">{label}</label>
      <Input value={value || ""} onChange={onChange} data-testid={testid} />
    </div>
  );
}

function TextField({ label, value, onChange, testid, mono }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#5C544F]">{label}</label>
      <Textarea value={value || ""} onChange={onChange} rows={4} className={mono ? "font-mono text-xs" : ""} data-testid={testid} />
    </div>
  );
}
